import {PageStandard} from "../utils/PageStandard";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormLabel,
    IconButton,
    Input,
    Modal,
    ModalClose,
    Sheet,
    Stack,
    Typography
} from "@mui/joy";
import {Fragment, useEffect, useMemo, useState} from "react";
import {CategoriaModel} from "../utils/Model";
import {ArrowDownward, ArrowUpward, DeleteForever, Edit, FileDownload, Search, SwapVert} from "@mui/icons-material";
import EmojiPicker from "emoji-picker-react";
import {addCategory, deleteCategory, editCategoria, getCategories} from "../utils/ViewModel";
import {downloadCSV, hasChanges} from "../utils/Utils";
import moment from "moment";

const GRID_TEMPLATE_COLUMNS = "88px minmax(200px, 1.6fr) minmax(220px, 1.6fr) minmax(96px, 0.6fr)"

const HEADER_CELL_SX = {
    px: 1.5,
    py: 1,
    fontWeight: "lg",
    fontSize: "sm",
    bgcolor: "background.level2",
    borderBottom: "1px solid",
    borderColor: "divider",
    display: "flex",
    alignItems: "center",
    gap: 0.5,
} as const

const CELL_SX = {
    px: 1.5,
    py: 1,
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid",
    borderColor: "divider",
} as const

export function Categorie() {

    const [modalAddCategoria, setModalAddCategoria] = useState(false);
    const initialDataNewCategoria = {
        title: "",
        icon: "",
    }
    const [newCategoria, setNewCategoria] = useState(initialDataNewCategoria)
    const [categorie, setCategorie] = useState<CategoriaModel[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState("")
    const [sortDir, setSortDir] = useState<"asc" | "desc" | null>(null)

    useEffect(() => {
        loadData()
    }, [])

    function loadData() {
        getCategories().then(data => {
            setCategorie(data);
            setLoading(false)
        });
    }

    function toggleSort() {
        setSortDir(dir => dir === null ? "asc" : dir === "asc" ? "desc" : null)
    }

    const searchLower = search.trim().toLowerCase()

    const filteredCategorie = useMemo(() => {
        const filtered = categorie.filter(c =>
            searchLower === "" || c.title.toLowerCase().includes(searchLower)
        )
        if (sortDir === null) return filtered
        const sorted = [...filtered].sort((a, b) => a.title.localeCompare(b.title))
        return sortDir === "asc" ? sorted : sorted.reverse()
    }, [categorie, searchLower, sortDir])

    return (
        <PageStandard title="Categorie" sidebarVisible={true} headerEnd={
            <Stack direction="row" spacing={2}>
                <Button onClick={() => setModalAddCategoria(true)}>Aggiungi categoria</Button>
                <Button startDecorator={<FileDownload/>}
                        onClick={() => {
                            downloadCSV<CategoriaModel>({
                                data: categorie,
                                fields: ["id", "title", "icon"],
                                filename: `oasi-categorie-${moment().format("YYYY-MM-DD")}.csv`,
                            })
                        }}
                >
                    Esporta tutti in CSV
                </Button>
            </Stack>
        }>
            <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 2}} flexWrap="wrap">
                <Input
                    placeholder="Cerca per titolo..."
                    startDecorator={<Search/>}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{minWidth: 280, flexGrow: 1, maxWidth: 420}}
                />
                <Chip size="sm" variant="soft" color="neutral">{categorie.length} categorie</Chip>
            </Stack>

            {
                loading &&
                <Stack alignItems="center" justifyContent="center" sx={{py: 6}}>
                    <CircularProgress/>
                </Stack>
            }

            {
                !loading && filteredCategorie.length === 0 &&
                <Typography level="body-md" sx={{textAlign: "center", py: 4}}>
                    Nessuna categoria trovata{searchLower !== "" ? ` per "${search}"` : ""}.
                </Typography>
            }

            {
                !loading && filteredCategorie.length > 0 &&
                <Box sx={{overflowX: "auto", pb: 5}}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
                            minWidth: 720,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "md",
                            overflow: "hidden",
                        }}
                    >
                        <Box sx={HEADER_CELL_SX}/>
                        <Box
                            sx={{...HEADER_CELL_SX, cursor: "pointer", userSelect: "none"}}
                            onClick={toggleSort}
                        >
                            Titolo
                            {
                                sortDir === null ?
                                    <SwapVert sx={{fontSize: 16, opacity: 0.35}}/>
                                    :
                                    (sortDir === "asc" ? <ArrowUpward sx={{fontSize: 16}}/> :
                                        <ArrowDownward sx={{fontSize: 16}}/>)
                            }
                        </Box>
                        <Box sx={HEADER_CELL_SX}>ID</Box>
                        <Box sx={{...HEADER_CELL_SX, justifyContent: "center"}}>Azioni</Box>

                        {filteredCategorie.map((c, idx) => (
                            <RowCategoria key={c.id} data={c} loadData={loadData} zebra={idx % 2 === 1}/>
                        ))}
                    </Box>
                </Box>
            }

            <Modal
                open={modalAddCategoria}
                onClose={() => setModalAddCategoria(false)}
                sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}
            >
                <Sheet
                    variant="outlined"
                    sx={{minWidth: 500, borderRadius: 'md', p: 3, boxShadow: 'lg'}}
                >
                    <ModalClose variant="plain" sx={{m: 1}}/>
                    <Typography component="h2" level="h4"
                                sx={{fontWeight: 'lg', mb: 1}}
                    >
                        {newCategoria.icon !== "" && newCategoria.icon} Aggiungi categoria
                    </Typography>
                    <Stack direction="column" spacing={2}>
                        <FormControl>
                            <FormLabel>Icona
                                categoria{newCategoria.icon !== "" && ` - Attuale: ${newCategoria.icon}`}</FormLabel>
                            <EmojiPicker
                                onEmojiClick={emoji => setNewCategoria({...newCategoria, icon: emoji.emoji})}
                                skinTonesDisabled
                                width="100%"
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Titolo categoria</FormLabel>
                            <Input type="text"
                                   value={newCategoria.title}
                                   onChange={(e) => setNewCategoria({...newCategoria, title: e.target.value})}
                                   placeholder="Dormire"
                            />
                        </FormControl>
                        <Button fullWidth
                                disabled={newCategoria.title.trim().length === 0 || newCategoria.icon.trim().length === 0}
                                onClick={() => {
                                    addCategory(newCategoria.title.trim(), newCategoria.icon.trim())
                                        .then(() => {
                                            loadData()
                                            setNewCategoria(initialDataNewCategoria)
                                            setModalAddCategoria(false)
                                        })
                                }}
                        >
                            Aggiungi
                        </Button>
                    </Stack>
                </Sheet>
            </Modal>
        </PageStandard>
    )
}

function RowCategoria(props: { data: CategoriaModel, loadData: () => void, zebra: boolean }) {
    const loadData = props.loadData;
    const defaultdata = props.data

    const [data, setData] = useState<CategoriaModel>(props.data);
    const [modalEdit, setModalEdit] = useState(false)

    const zebraSx = props.zebra ? {bgcolor: "background.level1"} : undefined

    return (
        <Fragment>
            <Box sx={{...CELL_SX, ...zebraSx, justifyContent: "center"}}>
                <Typography level="h3">{defaultdata.icon}</Typography>
            </Box>
            <Box sx={{...CELL_SX, ...zebraSx}}>
                <Typography level="body-sm" fontWeight="lg">{defaultdata.title}</Typography>
            </Box>
            <Box sx={{...CELL_SX, ...zebraSx}}>
                <Typography level="body-xs" sx={{fontFamily: "monospace"}} noWrap>{defaultdata.id}</Typography>
            </Box>
            <Box sx={{...CELL_SX, ...zebraSx, justifyContent: "center", gap: 1}}>
                <IconButton color="primary" variant="outlined" size="sm"
                            onClick={() => setModalEdit(true)}
                >
                    <Edit fontSize="small"/>
                </IconButton>
                <IconButton color="danger" variant="outlined" size="sm"
                            onClick={() => {
                                if (window.confirm(`Eliminare definitivamente la categoria ${defaultdata.icon} ${defaultdata.title} ?`)) {
                                    deleteCategory(defaultdata.id)
                                        .then(() => {
                                            loadData()
                                        })
                                }
                            }}
                >
                    <DeleteForever fontSize="small"/>
                </IconButton>
            </Box>

            <Modal
                open={modalEdit}
                onClose={() => cancelModal()}
                sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}
            >
                <Sheet
                    variant="outlined"
                    sx={{minWidth: 500, borderRadius: 'md', p: 3, boxShadow: 'lg'}}
                >
                    <ModalClose variant="plain" sx={{m: 1}}/>
                    <Typography component="h2" level="h4"
                                sx={{fontWeight: 'lg', mb: 1}}
                    >
                        {data.icon !== "" && data.icon} Modifica categoria
                    </Typography>
                    <Typography level="body-sm" sx={{fontFamily: "monospace", mb: 2}}>
                        ID: {defaultdata.id}
                    </Typography>
                    <Stack direction="column" spacing={2}>
                        <FormControl>
                            <FormLabel>Icona categoria{data.icon !== "" && ` - Attuale: ${data.icon}`}</FormLabel>
                            <EmojiPicker
                                onEmojiClick={emoji => setData({...data, icon: emoji.emoji})}
                                skinTonesDisabled
                                width="100%"
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Titolo categoria</FormLabel>
                            <Input type="text"
                                   value={data.title}
                                   onChange={(e) => setData({...data, title: e.target.value})}
                                   placeholder="Dormire"
                            />
                        </FormControl>
                        <Stack direction="row" spacing={2}>
                            <Button fullWidth
                                    variant="outlined"
                                    color="neutral"
                                    onClick={() => cancelModal()}
                            >
                                Annulla
                            </Button>
                            <Button fullWidth
                                    disabled={data.title.trim().length === 0 || data.icon.trim().length === 0 || !hasChanges(props.data, data)}
                                    onClick={() => {
                                        editCategoria(data)
                                            .then(() => {
                                                loadData()
                                                setModalEdit(false)
                                            })
                                    }}
                            >
                                Modifica
                            </Button>
                        </Stack>
                    </Stack>
                </Sheet>
            </Modal>
        </Fragment>
    )

    function cancelModal() {
        setModalEdit(false)
        setData(defaultdata)
    }
}
